import { useEffect, useState } from "react";
import type { UIMessage } from "@ai-sdk/react";
import { Brain } from "lucide-react";
import {
  getLastRenderablePart,
  getToolPartName,
  isToolLikePart,
  isToolPartRunning,
} from "./chatToolParts";

export function ThinkingBlock({ compact }: { compact?: boolean }) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-lg border border-border/15 bg-muted/20 ${compact ? "max-w-[80%] mr-auto px-2.5 py-1.5" : "px-3 py-2"}`}
    >
      <Brain className={`shrink-0 text-purple-400/70 ${compact ? "w-3 h-3" : "w-3.5 h-3.5"}`} />
      <span className={`font-medium text-muted-foreground/60 ${compact ? "text-[10px]" : "text-xs"}`}>
        Thought for a moment
      </span>
    </div>
  );
}

function AgentThinkingTimer({ name, toolKey }: { name: string; toolKey: string }) {
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    const start = Date.now();
    setElapsedMs(0);
    const id = setInterval(() => setElapsedMs(Date.now() - start), 250);
    return () => clearInterval(id);
  }, [toolKey]);
  const totalSec = Math.floor(elapsedMs / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  const label = mm > 0 ? `${mm}:${String(ss).padStart(2, "0")}` : `${ss}s`;
  return (
    <div className="flex items-center gap-3 text-muted-foreground bg-card border border-border/50 px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm w-fit max-w-[85%]">
      <div className="relative w-5 h-5 shrink-0">
        <span className="absolute inset-0 rounded-full bg-purple-500/20 animate-ping" />
        <Brain className="relative w-5 h-5 text-purple-400" />
      </div>
      <div className="flex flex-col leading-tight">
        <span className="text-[13px] font-semibold text-foreground">
          {name} is thinking…
        </span>
        <span className="ds-micro text-muted-foreground/70 tabular-nums mt-0.5">
          {label}
        </span>
      </div>
    </div>
  );
}

const THINKING_TOOL_NAMES = new Set([
  "ads_plan_ugc_elements",
  "ads_generate_ugc_element_refs",
]);

function detectActiveThinkingTool(messages: UIMessage[]): string | null {
  for (let m = messages.length - 1; m >= 0; m -= 1) {
    const msg = messages[m];
    if (msg.role !== "assistant") continue;
    for (const part of msg.parts ?? []) {
      if (!isToolLikePart(part)) continue;
      const name = getToolPartName(part);
      if (!name || !THINKING_TOOL_NAMES.has(name)) continue;
      if (part.state === "input-streaming" || part.state === "input-available") {
        return name;
      }
    }
  }
  return null;
}

function TypingDots() {
  return (
    <div className="flex gap-2 items-center text-muted-foreground bg-card border border-border/50 px-5 py-4 rounded-2xl rounded-bl-sm shadow-sm w-fit max-w-[85%]">
      <span
        className="w-2 h-2 rounded-full bg-foreground/40 animate-bounce"
        style={{ animationDelay: "0ms" }}
      />
      <span
        className="w-2 h-2 rounded-full bg-foreground/40 animate-bounce"
        style={{ animationDelay: "150ms" }}
      />
      <span
        className="w-2 h-2 rounded-full bg-foreground/40 animate-bounce"
        style={{ animationDelay: "300ms" }}
      />
    </div>
  );
}

export function ChatTypingIndicator({
  messages,
  isLoading,
  hideTypingIndicator,
  name,
}: {
  messages: UIMessage[];
  isLoading?: boolean;
  hideTypingIndicator?: boolean;
  name: string;
}) {
  const activeThinkingTool = detectActiveThinkingTool(messages);
  if (activeThinkingTool) {
    return <AgentThinkingTimer name={name} toolKey={activeThinkingTool} />;
  }
  if (!isLoading || hideTypingIndicator) return null;
  if (isToolPartRunning(getLastRenderablePart(messages))) return null;
  return <TypingDots />;
}
